import type { CSSProperties } from 'react'

export type CalendarEventTags = string[] | string | null | undefined

const GOOGLE_GRADIENT = 'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.86) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.72) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.68) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.76) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(135deg, #78adfb 0%, #f6a79d 38%, #ffe289 68%, #6cc889 100%)'
const OUTLOOK_GRADIENT = 'linear-gradient(135deg, #034fa0, #0078d4, #249ee4)'
const REMINDER_GRADIENT = 'linear-gradient(135deg, #2bb673, #9ae6b4)'
const MEETING_GRADIENT = 'linear-gradient(135deg, #e05858, #f6a36a)'
const DEFAULT_GRADIENT = 'linear-gradient(135deg, #6481d2, #78f2c8)'

function normalizeTags(tags: CalendarEventTags) {
  const list = Array.isArray(tags) ? tags : typeof tags === 'string' ? [tags] : []
  return list.map((tag) => tag.toLowerCase())
}

export function getEventGradient(tags: CalendarEventTags) {
  const normalized = normalizeTags(tags)
  const isGoogle = normalized.some((tag) => tag.includes('google'))
  const isOutlook = normalized.some(
    (tag) => tag.includes('azure') || tag.includes('outlook') || tag.includes('microsoft'),
  )
  const isReminder = normalized.some((tag) => tag.includes('reminder'))
  const isMeeting = normalized.some((tag) => tag.includes('meeting'))

  if (isGoogle) return GOOGLE_GRADIENT
  if (isOutlook) return OUTLOOK_GRADIENT
  if (isReminder) return REMINDER_GRADIENT
  if (isMeeting) return MEETING_GRADIENT
  return DEFAULT_GRADIENT
}

export function getEventStyle(tags: CalendarEventTags) {
  const normalized = normalizeTags(tags)
  const gradient = getEventGradient(tags)
  const isGoogle = normalized.some((tag) => tag.includes('google'))
  const isOutlook = normalized.some(
    (tag) => tag.includes('azure') || tag.includes('outlook') || tag.includes('microsoft'),
  )
  const isReminder = normalized.some((tag) => tag.includes('reminder'))
  const isMeeting = normalized.some((tag) => tag.includes('meeting'))
  const boxShadow = isGoogle
    ? '0 10px 18px rgba(66, 133, 244, 0.2)'
    : isOutlook
      ? '0 10px 18px rgba(0, 120, 212, 0.22)'
      : isReminder
        ? '0 10px 18px rgba(43, 182, 115, 0.22)'
        : isMeeting
          ? '0 10px 18px rgba(224, 88, 88, 0.22)'
          : '0 10px 18px rgba(100, 129, 210, 0.18)'
  const hoverShadow = isGoogle
    ? '0 14px 24px rgba(66, 133, 244, 0.28)'
    : isOutlook
      ? '0 14px 24px rgba(0, 120, 212, 0.28)'
      : isReminder
        ? '0 14px 24px rgba(43, 182, 115, 0.28)'
        : isMeeting
          ? '0 14px 24px rgba(224, 88, 88, 0.28)'
          : '0 14px 24px rgba(100, 129, 210, 0.25)'

  return {
    style: {
      background: gradient,
      '--event-bg': gradient,
      '--event-shadow': boxShadow,
      '--event-shadow-hover': hoverShadow,
      '--event-pill-gradient': gradient,
    } as CSSProperties,
  }
}
